import { ArchivedItemType } from '../enums/archived-items.enum'

export type ArchivedItemTypeFilter = 'all' | ArchivedItemType

export type ArchivedItemDateRangeFilter = 'any' | 'today' | 'last_7_days' | 'last_30_days' | 'custom'

export type ArchivedItemsFilters = {
  type: ArchivedItemTypeFilter
  dateRange: ArchivedItemDateRangeFilter
  customArchivedFrom: string | null
  customArchivedTo: string | null
}

export type ArchivedItemsQueryFilters = {
  type?: string
  archivedFrom?: string
  archivedTo?: string
}

export const DEFAULT_ARCHIVED_ITEMS_FILTERS: ArchivedItemsFilters = {
  type: 'all',
  dateRange: 'any',
  customArchivedFrom: null,
  customArchivedTo: null
}

export const ARCHIVED_ITEM_TYPE_FILTER_OPTIONS: ReadonlyArray<{ value: ArchivedItemTypeFilter; label: string }> = [
  { value: 'all', label: 'All types' },
  { value: ArchivedItemType.PERSONA, label: 'Persona' }
  // TODO: Uncomment this when sessions are supported
  // { value: ArchivedItemType.SESSION, label: 'Session' }
]

export const ARCHIVED_ITEM_DATE_RANGE_FILTER_OPTIONS: ReadonlyArray<{ value: ArchivedItemDateRangeFilter; label: string }> = [
  { value: 'any', label: 'Any time' },
  { value: 'today', label: 'Today' },
  { value: 'last_7_days', label: 'Last 7 days' },
  { value: 'last_30_days', label: 'Last 30 days' },
  { value: 'custom', label: 'Custom range' }
]

/**
 * Count the active archived item filters.
 * @param filters {ArchivedItemsFilters} - The filters.
 * @returns {number} - The count of active filters.
 */
export function countActiveArchivedItemFilters(filters: ArchivedItemsFilters): number {
  let count = 0
  if (filters.type !== 'all') count++
  if (filters.dateRange === 'custom') {
    if (filters.customArchivedFrom && filters.customArchivedTo) count++
  } else if (filters.dateRange !== 'any') {
    count++
  }
  return count
}

/**
 * Convert the filters to query filters.
 * @param filters {ArchivedItemsFilters} - The filters.
 * @returns {ArchivedItemsQueryFilters | undefined} - The query filters.
 */
export function toArchivedItemsQueryFilters(filters: ArchivedItemsFilters): ArchivedItemsQueryFilters | undefined {
  const query: ArchivedItemsQueryFilters = {}

  if (filters.type !== 'all') {
    query.type = filters.type
  }

  const bounds = getArchivedItemDateRangeBounds(filters)
  if (bounds?.archivedFrom) query.archivedFrom = bounds.archivedFrom
  if (bounds?.archivedTo) query.archivedTo = bounds.archivedTo

  return Object.keys(query).length ? query : undefined
}

/**
 * Format a custom archived date range for filter labels.
 * @param from {string | null} - The start date (`YYYY-MM-DD`).
 * @param to {string | null} - The end date (`YYYY-MM-DD`).
 * @returns {string} - The formatted label.
 */
export function formatArchivedItemCustomDateRangeLabel(from: string | null, to: string | null): string {
  if (!from || !to) return 'Custom range'

  const format = (ymd: string): string => {
    const date = new Date(`${ymd}T12:00:00`)
    if (isNaN(date.getTime())) return ymd
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
  }

  return `${format(from)} - ${format(to)}`
}

/**
 * Format the date.
 * @param date {Date} - The date.
 * @returns {string} - The formatted date.
 */
export function formatArchivedItemDate(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * Today's date as `YYYY-MM-DD` in local time.
 * @returns {string} - Today's date.
 */
export function getArchivedItemTodayYmd(): string {
  return formatArchivedItemDate(new Date())
}

/**
 * Get the archived item date range bounds.
 * @param filters {ArchivedItemsFilters} - The filters.
 * @returns {ArchivedItemsQueryFilters | null} - The archived item date range bounds.
 */
function getArchivedItemDateRangeBounds(filters: ArchivedItemsFilters): ArchivedItemsQueryFilters | null {
  const { dateRange, customArchivedFrom, customArchivedTo } = filters

  if (dateRange === 'any') return null

  if (dateRange === 'custom') {
    if (!customArchivedFrom || !customArchivedTo) return null
    return { archivedFrom: customArchivedFrom, archivedTo: customArchivedTo }
  }

  const today = new Date()
  const archivedTo = formatArchivedItemDate(today)

  if (dateRange === 'today') {
    return { archivedFrom: archivedTo, archivedTo }
  }

  const from = new Date(today)
  from.setDate(from.getDate() - (dateRange === 'last_7_days' ? 6 : 29))

  return { archivedFrom: formatArchivedItemDate(from), archivedTo }
}
